import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { getTicketsByParkingLocation } from "api/canpark/tickets/getTicketsByParkingLocation";
import { TicketFilter } from "./TicketsTableFilterBar";
import { ParkingLocation } from "../../types/parkingLocation";
import * as XLSX from "xlsx";

interface TicketsExportModalProps {
  open: boolean;
  onClose: () => void;
  parkingLocation: ParkingLocation;
  filter: TicketFilter;
}

const TicketsExportModal: React.FC<TicketsExportModalProps> = ({
  open,
  onClose,
  parkingLocation,
  filter,
}) => {
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState<any[]>([]);
  const [excelBlob, setExcelBlob] = useState<Blob | null>(null);

  useEffect(() => {
    if (!open) {
      setTickets([]);
      setExcelBlob(null);
      setLoading(false);
      return;
    }
    setLoading(true);

    // Fetch all tickets (ignore pagination)
    const fetchAllTickets = async () => {
      try {
        const allTickets = await getTicketsByParkingLocation(
          parkingLocation.id,
          {
            ...filter,
            items_per_page: 999999, // Large number to fetch all
            page: 1,
          }
        );
        setTickets(allTickets);

        // Generate Excel
        const worksheet = XLSX.utils.json_to_sheet(
          allTickets.map((t: any) => ({
            "Record #": t.ticketNumber,
            Type: t.ticketType,
            "License Plate": t.licenseNumber,
            State: t.licenseState,
            Date: t.date,
            "Zone/Address": t.parkingZone,
            Vehicle: t.vehicleInfo,
            "Issued By": t.issuedBy,
            Comments: t.comments,
            Attachments: t.attachments?.length || 0,
            "Is Void": t.isVoid ? "Yes" : "No",
          }))
        );
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Records");
        const wbout = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
        setExcelBlob(new Blob([wbout], { type: "application/octet-stream" }));
      } catch (e) {
        setTickets([]);
        setExcelBlob(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAllTickets();
    // eslint-disable-next-line
  }, [open, parkingLocation.id, filter]);

  const handleDownload = () => {
    if (!excelBlob) return;
    const url = window.URL.createObjectURL(excelBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `record-data-${parkingLocation.name.replace(/\s+/g, "-")}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Download Record Data</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: 120,
        }}
      >
        {loading ? (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Fetching record data...</Typography>
          </>
        ) : (
          <>
            <Typography sx={{ mb: 2, pt: 4 }}>
              {tickets.length
                ? `Ready to download ${tickets.length} records.`
                : "No records found."}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          color="primary"
          variant="contained"
          disabled={loading || !excelBlob}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketsExportModal;
